import { Ajax2 } from '@shared/Ajax';
import { Auth } from '@shared/Auth';
import SparkMD5 from 'spark-md5';

export interface S3PresignResult {
    uploadUrl: string; // https://s3.....amazonaws.com/foo.infrakit.info/file-service/2023/08-27/uuid
    contentType: string;
    expires: string;
    document: {
        uuid: string;
        headUuid: string;
        name: string;
        folderPath: string; // "/Alignments/etc/etc"
        uuidPath: string; // "/bff26d31-9b51-11ea-99a3-01ca6403320/bff28355-9b51-11ea-99a3-06es6403320"
        timestamp: string; // "2023-08-27T11:51:30.055Z"
        state: 'PENDING_UPLOAD' | string;
        filesize: number;
        md5: number;
    };

    // calculated by service
    checksumMd5b64: string;
}

export interface DroneDatasetFileInput {
    file: File;
    fileType: string;
}

interface PreparedUploadFile {
    file: File;
    fileType: string;
    checksum: string;
    checksumMd5b64: string;
    contentType: string;
    size: number;
}

export interface DroneDatasetCreateResult {
    datasetUuid: string;
    captureDate: string;
    files: S3PresignResult[];
}

const refreshToken = _.debounce(async () => {
    console.warn('Token expired, refreshing');
    await Auth.refreshToken();
}, 500);

export const FileServiceClient = {
    getDownloadUrl: async (objectKey: string, bucketName?: string): Promise<string> => {
        try {
            const token = Auth.getStoredToken();

            if (Auth.isTokenExpired(token)) {
                await refreshToken();
            }

            const resp = await Ajax2({
                url: `${kuura.getContextPath()}/v1/file/async-download`,
                method: 'POST',
                useToken: true,
                data: {
                    bucketName: bucketName,
                    objectKey: objectKey,
                },
            });

            return resp.downloadUrl;
        } catch (e) {
            console.warn('Encountered an error when fetching download link from File Service');
            return null;
        }
    },

    getS3UploadPath: async (
        file: File,
        opts: {
            fileType: string;
            folderUuid: string;
            filename: string;
            headUuid?: string; // for new versions
            description?: string;
            checksum?: string;
            comment?: string;
            contentType?: string;
            direction?: number;
            originalTimestamp?: number;
            altitude?: number;
            reportTemplateId?: number;
            size?: number;
            lat?: number;
            lon?: number;
            properties?: Record<string, any>;
            deflate?: boolean;
        }
    ): Promise<S3PresignResult> => {
        console.log('getS3UploadPath for file:', file, 'options:', opts);

        try {
            const preparedFile = await prepareFileUpload(file, opts.contentType);
            opts.checksum = preparedFile.checksum;
            opts.size = preparedFile.size;
            opts.contentType = preparedFile.contentType;

            return Ajax2({
                url: `${kuura.getContextPath()}/v1/${opts.fileType}/async-upload`,
                method: 'POST',
                contentType: 'json',
                useToken: true,
                data: opts,
            })
                .then(resp => {
                    resp.checksumMd5b64 = preparedFile.checksumMd5b64;
                    resp.contentType = preparedFile.contentType;
                    return resp;
                })
                .catch(e => {
                    console.log('getS3UploadPath /async-upload error:', e);
                    throw e; // uploader also needs to react to error
                });
        } catch (e) {
            console.log('e:', e);
        }
    },

    createDroneDataset: async (folderUuid: string, files: DroneDatasetFileInput[], captureDate: number): Promise<DroneDatasetCreateResult> => {
        const preparedFiles = await Promise.all(files.map(file => prepareDatasetFile(file)));
        const response = await Ajax2({
            url: `${kuura.getContextPath()}/v1/drone-dataset/async-upload`,
            method: 'POST',
            contentType: 'json',
            useToken: true,
            data: {
                folderUuid,
                captureDate: new Date(captureDate).toISOString(),
                files: preparedFiles.map(file => ({
                    filename: file.file.name,
                    contentType: file.contentType,
                    size: file.size,
                    checksum: file.checksum,
                    fileType: file.fileType,
                    deflate: false,
                })),
            },
        });

        for (let i = 0; i < response.files.length; i++) {
            await uploadFileToS3(preparedFiles[i], response.files[i]);
        }

        return response;
    },
};

// needs to be done incrementally because chrome has 2gb limit for arraybuffers and we support >2gb files
function hashIncremental(file: File, raw: boolean): Promise<string> {
    return new Promise((done, fail) => {
        let blobSlice = File.prototype.slice;
        let chunkSize = 2097152; // Read in chunks of 2MB
        let chunks = Math.ceil(file.size / chunkSize);
        let currentChunk = 0;
        let spark = new SparkMD5.ArrayBuffer();
        let fileReader = new FileReader();

        fileReader.onload = function (e) {
            spark.append(e.target.result);
            currentChunk++;

            if (currentChunk < chunks) {
                loadNext();
            } else {
                done(spark.end(raw));
            }
        };

        fileReader.onerror = () => fail('hashing failed');

        function loadNext() {
            let start = currentChunk * chunkSize;
            let end = start + chunkSize >= file.size ? file.size : start + chunkSize;

            fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
        }

        loadNext();
    });
}

async function prepareFileUpload(file: File, contentType?: string): Promise<Omit<PreparedUploadFile, 'fileType' | 'file'>> {
    const checksum = await hashIncremental(file, false);
    const checksumMd5b64 = btoa(await hashIncremental(file, true));
    return {
        checksum,
        checksumMd5b64,
        contentType: contentType?.length ? contentType : file.type?.length ? file.type : 'application/octet-stream',
        size: file.size,
    };
}

async function prepareDatasetFile(file: DroneDatasetFileInput): Promise<PreparedUploadFile> {
    const preparedUpload = await prepareFileUpload(file.file);
    return {
        ...preparedUpload,
        file: file.file,
        fileType: file.fileType,
    };
}

async function uploadFileToS3(file: PreparedUploadFile, upload: S3PresignResult): Promise<void> {
    const response = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': file.contentType,
            'Content-MD5': file.checksumMd5b64,
        },
        body: file.file,
    });

    if (!response.ok) {
        throw new Error(`Failed to upload ${file.file.name}`);
    }
}
